import { getProcessorsByUser, deleteAnnualProduction, postAnnualProduction, getAnnualProduction, putAnnualProduction } from '@/api/dataentry-api-generated';
import DeleteDialog from '@/components/DeleteDialog.vue';
import SaveDialog from '@/components/SaveDialog.vue';
import { defineComponent } from '@vue/runtime-core';
export default defineComponent({
    name: 'AnnualProductionComponent',
    components: { DeleteDialog, SaveDialog },
    props: {
        propSelected: Array,
        propCreateNew: Boolean,
        propDuplicateRecord: Boolean,
        propId: String
    },
    data: function () {
        return {
            filter: '',
            effectiveDates: '',
            createNew: this.propCreateNew,
            duplicateRecord: this.propDuplicateRecord,
            id: this.propId,
            loading: false,
            processors: [],
            item: {},
            processorOptions: [{
                    label: '',
                    value: ''
                }],
            processorDisplay: {
                label: '',
                value: ''
            },
            tablePagination: {
                sortBy: 'site',
                descending: true,
                rowsPerPage: 50
            },
            speciesList: [],
            speciesOptions: ['BOVINE', 'OVINE', 'PORCINE', 'CAPRINE', 'CALVES', 'LAMBS', 'OTHER'],
            fbrOptions: [
                { value: 'SIDE_BONING', label: 'Side Boning' },
                { value: 'QUARTER_BONING', label: 'Quarter Boning' }
            ]
        };
    },
    computed: {
        uuid() {
            return this.$route.params.uuid;
        },
        selectedItems() {
            return this.propSelected;
        },
        buttonLabel() {
            return 'SAVE';
        },
        years() {
            const currentYear = new Date().getFullYear();
            const currentDate = new Date();
            // Set the financial year start date to July 1st
            const financialYearStartDate = new Date(currentYear, 6, 1);
            // Generate values from the past 20 years until the current year
            let yearOptions = Array.from({ length: 21 }, (_, i) => `${currentYear - i} (Jul 1 ${(currentYear - i) - 1} - Jun 30 ${currentYear - i})`);
            // Add an option for the 2025 financial year if July 1st has already passed
            if (currentDate > financialYearStartDate) {
                yearOptions.unshift(`${currentYear + 1} (Jul 1 ${currentYear} - Jun 30 ${currentYear + 1})`);
            }
            return yearOptions;
        },
        toggleBovineSection() {
            return this.speciesList.includes('BOVINE');
        },
        toggleOvineSection() {
            return this.speciesList.includes('OVINE');
        },
        togglePorcineSection() {
            return this.speciesList.includes('PORCINE');
        },
        toggleCaprineSection() {
            return this.speciesList.includes('CAPRINE');
        },
        toggleCalvesSection() {
            return this.speciesList.includes('CALVES');
        },
        toggleLambsSection() {
            return this.speciesList.includes('LAMBS');
        },
        toggleOtherSection() {
            return this.speciesList.includes('OTHER');
        }
    },
    created() {
        this.getProcessors();
        if (!this.createNew) {
            this.getItem(this.uuid, true);
        }
        if (this.duplicateRecord) {
            if (this.id !== undefined) {
                this.getItem(this.id, false);
            }
        }
    },
    methods: {
        async getItem(uuid, isUpdate) {
            this.loading = true;
            try {
                const result = await getAnnualProduction(uuid);
                this.item = result;
                if (isUpdate) {
                    this.item.yearEntry = result.yearEntry;
                    this.processorDisplay.label = result.environmentProcessorLabel;
                    this.processorDisplay.value = result.processor;
                }
                else {
                    this.item.yearEntry = new Date().getFullYear();
                }
                this.loading = false;
            }
            catch (error) {
                const errMsg = error;
                if (errMsg.message.includes('ResourceNotFoundException')) {
                    this.closeDialog();
                    this.$log.addError(error);
                    this.loading = false;
                }
            }
        },
        saveItem() {
            let isCartonsAndCratesPercentageTotalValid = false;
            // Validate that the cartons and crates percentage total add up to 100%
            if ((this.item.cartonsPercentage !== undefined && this.item.cartonsPercentage !== null) &&
                (this.item.cratesPercentage !== undefined && this.item.cratesPercentage !== null)) {
                const cartonsAndCratesPercentageTotal = this.item.cartonsPercentage + this.item.cratesPercentage;
                if (cartonsAndCratesPercentageTotal !== 100) {
                    const msg = 'The cartons and crates percentages must equal 100% in total.';
                    const errorMsg = {
                        type: 'warning',
                        textColor: 'black',
                        timeout: 5000,
                        position: 'top',
                        message: msg,
                        html: true
                    };
                    this.$log.addCustomMessage(errorMsg);
                }
                else {
                    isCartonsAndCratesPercentageTotalValid = true;
                }
            }
            if (isCartonsAndCratesPercentageTotalValid) {
                // Save or update the item
                if (!this.createNew) {
                    this.updateItem();
                }
                else {
                    this.createItem();
                }
            }
        },
        async updateItem() {
            // Validate the form before proceeding
            const valid = await this.$refs.form.validate();
            if (valid) {
                this.loading = true;
                const req = this.item;
                req.yearEntry = this.item.yearEntry;
                try {
                    const req = this.item;
                    req.processor = this.processorDisplay.value;
                    await putAnnualProduction(this.uuid, this.item);
                    this.$log.addMessage('Successfully updated Annual Production');
                    this.$router.push({ name: 'Annual Productions' });
                }
                catch (error) {
                    this.$log.addError(error);
                    this.loading = false;
                }
            }
        },
        async createItem() {
            const valid = await this.$refs.form.validate();
            if (valid) {
                this.loading = true;
                this.setDefaultValues();
                const req = this.item;
                req.yearEntry = this.item.yearEntry;
                req.processor = this.processorDisplay.value;
                try {
                    await postAnnualProduction(req);
                    this.$log.addMessage('Successfully created new Annual Production');
                    this.$emit('refresh');
                    this.closeDialog();
                }
                catch (error) {
                    const errorObj = error;
                    if (errorObj.message.includes('ResourceAlreadyExistsException')) {
                        const uuid = errorObj.message.substring(31, errorObj.message.length);
                        const msg = `A record with the same Annual Production already exists: <a href="#/AnnualProduction/${uuid}">VIEW</a>`;
                        const errorMsg = {
                            type: 'warning',
                            textColor: 'black',
                            timeout: 5000,
                            position: 'top',
                            message: msg,
                            html: true
                        };
                        this.$log.addCustomMessage(errorMsg);
                    }
                    else {
                        this.$log.addError(error);
                    }
                    this.loading = false;
                }
            }
        },
        async deleteItem() {
            try {
                const result = await deleteAnnualProduction(this.uuid);
                this.item = result;
                this.$log.addMessage('Successfully deleted Annual Production');
                this.closeDialog();
            }
            catch (error) {
                this.$log.addError(error);
            }
        },
        closeDialog() {
            if (this.createNew) {
                this.$emit('show-hide-dialog');
            }
            else {
                this.goBack();
            }
        },
        goBack() {
            this.$router.push({ name: 'Annual Productions' });
        },
        async getProcessors() {
            this.loading = true;
            try {
                const result = await getProcessorsByUser({ pageSize: 1000000, deleted: false });
                this.processors = result.items;
                this.processorOptions = this.getProcessorOptions();
                this.loading = false;
            }
            catch (error) {
                this.$log.addError(error);
                this.loading = false;
            }
        },
        getProcessorOptions() {
            // Display and sort the options in alphabetical order
            return this.processors.map(i => {
                return {
                    label: i.processorLabel,
                    value: i.uuid
                };
            }).sort((a, b) => a.label.localeCompare(b.label));
        },
        filterOptions(val, update) {
            if (val === '') {
                update(() => {
                    // No filter - display default list
                    this.processorOptions = this.getProcessorOptions();
                });
                return;
            }
            update(() => {
                const needle = val.toLowerCase();
                // Sort the search results in alphabetical order
                this.processorOptions = this.getProcessorOptions()
                    .filter(v => v.label.toLowerCase().indexOf(needle) > -1)
                    .sort((a, b) => a.label.localeCompare(b.label));
            });
        },
        setDefaultValues() {
            // These dropdown fields have to be set as they cannot be null
            if (this.item.fabricationBoningRoomBovine === undefined) {
                this.item.fabricationBoningRoomBovine = 'SIDE_BONING';
            }
            if (this.item.fabricationBoningRoomOvine === undefined) {
                this.item.fabricationBoningRoomOvine = 'SIDE_BONING';
            }
            if (this.item.fabricationBoningRoomPorcine === undefined) {
                this.item.fabricationBoningRoomPorcine = 'SIDE_BONING';
            }
            if (this.item.fabricationBoningRoomCaprine === undefined) {
                this.item.fabricationBoningRoomCaprine = 'SIDE_BONING';
            }
            if (this.item.fabricationBoningRoomCalves === undefined) {
                this.item.fabricationBoningRoomCalves = 'SIDE_BONING';
            }
            if (this.item.fabricationBoningRoomLambs === undefined) {
                this.item.fabricationBoningRoomLambs = 'SIDE_BONING';
            }
            if (this.item.fabricationBoningRoomOther === undefined) {
                this.item.fabricationBoningRoomOther = 'SIDE_BONING';
            }
        }
    }
});
